import {
  BookOpenText,
  Briefcase,
  ClockCounterClockwise,
  Files,
  FunnelSimple,
  GraduationCap,
  IdentificationCard,
  ListStar,
  MapPin,
  NoteBlank,
  PaperPlaneTilt,
  Star,
  Tag,
  Translate,
  UserList,
} from "@phosphor-icons/react";

import { LeadSource, LinkedInAccountsTag } from "features/leads/components";

import type { Lead, LeadsSearchParams } from "common/types";

import DesktopLabels from "./components/AllLeads/DesktopLayout/DesktopLabels";
import LeadCompanyTimeTag from "./components/AllLeads/LeadTags/LeadCompanyTimeTag";
import LeadDetailTag from "./components/AllLeads/LeadTags/LeadDetailTag";
import MobileLabels from "./components/AllLeads/MobileLayout/MobileLabels";

import type { Icon } from "@phosphor-icons/react";
import type { JSX } from "react";

export const MANAGE_LEADS_ACTIONS = {
  labels: {
    icon: Tag,
    getLabel: () => "Add labels",
    getEligibleItems: (selection: Lead[]) => selection,
    isPopup: true,
  },
  notes: {
    icon: NoteBlank,
    getLabel: () => "Add notes",
    getEligibleItems: (selection: Lead[]) => selection,
  },
  favorite: {
    icon: Star,
    getLabel: () => "Favorite leads",
    getEligibleItems: (selection: Lead[]) =>
      selection.filter((lead) => !lead.favorite),
  },
  unfavorite: {
    icon: Star,
    getLabel: () => "Unfavorite leads",
    getEligibleItems: (selection: Lead[]) =>
      selection.every((lead) => lead.favorite) ? selection : [],
  },
} as const;

interface LeadColumn {
  label: string;
  icon: Icon;
  /** Preselected in the mobile tags drawer. */
  isDefault?: true;
  /** Renders the column's value */
  renderTag: (lead: Lead, isDesktop: boolean) => JSX.Element | null;
}

export const LEADS_COLUMNS = {
  occupation: {
    label: "Occupation",
    icon: IdentificationCard,
    isDefault: true,
    // on mobile the occupation is rendered inline, under the lead's name
    renderTag: ({ id }, isDesktop) =>
      isDesktop ? <LeadDetailTag leadId={id} leadKey="occupation" /> : null,
  },
  accounts: {
    label: "Accounts",
    icon: UserList,
    isDefault: true,
    renderTag: ({ lead_of }, isDesktop) => (
      <LinkedInAccountsTag
        accountIds={lead_of}
        size={isDesktop ? "md" : "sm"}
      />
    ),
  },
  origins: {
    label: "Origins",
    icon: FunnelSimple,
    renderTag: ({ id }, isDesktop) => (
      <LeadSource leadId={id} variant={isDesktop ? "plain" : "tagSm"} />
    ),
  },
  currentCompanies: {
    label: "Current company",
    icon: Briefcase,
    renderTag: ({ id }) => (
      <LeadDetailTag leadId={id} leadKey="current_experience" />
    ),
  },
  currentCompaniesTime: {
    label: "Current company time",
    icon: Briefcase,
    renderTag: ({ id }) => (
      <LeadCompanyTimeTag leadId={id} leadKey="current_experience" />
    ),
  },
  pastCompanies: {
    label: "Past company",
    icon: ClockCounterClockwise,
    renderTag: ({ id }) => (
      <LeadDetailTag leadId={id} leadKey="work_experience" />
    ),
  },
  pastCompaniesTime: {
    label: "Past company time",
    icon: ClockCounterClockwise,
    renderTag: ({ id }) => (
      <LeadCompanyTimeTag leadId={id} leadKey="work_experience" />
    ),
  },
  location: {
    label: "Location",
    icon: MapPin,
    renderTag: ({ id }) => <LeadDetailTag leadId={id} leadKey="location" />,
  },
  education: {
    label: "Education",
    icon: GraduationCap,
    renderTag: ({ id }) => <LeadDetailTag leadId={id} leadKey="education" />,
  },
  skills: {
    label: "Skills",
    icon: BookOpenText,
    renderTag: ({ id }) => <LeadDetailTag leadId={id} leadKey="skills" />,
  },
  interests: {
    label: "Interests",
    icon: ListStar,
    renderTag: ({ id }) => <LeadDetailTag leadId={id} leadKey="interests" />,
  },
  languages: {
    label: "Languages",
    icon: Translate,
    renderTag: ({ id }) => <LeadDetailTag leadId={id} leadKey="languages" />,
  },
  labels: {
    label: "Labels",
    icon: Tag,
    renderTag: ({ id, labels }, isDesktop) =>
      isDesktop ? (
        <DesktopLabels labels={labels} profileId={id} />
      ) : (
        <MobileLabels labels={labels} />
      ),
  },
} satisfies Record<string, LeadColumn>;

type FiltersType<Keys extends keyof LeadsSearchParams<never>> = {
  [K in Keys]: {
    icon: JSX.Element;
    label: string;
    isBoolean?: true;
  };
};

export const LEADS_FILTERS: FiltersType<
  keyof Omit<LeadsSearchParams<never>, "keywords">
> = {
  favorite: {
    icon: <Star weight="fill" />,
    label: "Favorite",
    isBoolean: true,
  },
  lead_of: { icon: <UserList weight="fill" />, label: "Account" },
  origins: { icon: <FunnelSimple weight="fill" />, label: "Origin" },
  current_companies: {
    icon: <Briefcase weight="fill" />,
    label: "Current company",
  },
  past_companies: {
    icon: <ClockCounterClockwise />,
    label: "Past company",
  },
  locations: {
    icon: <MapPin weight="fill" />,
    label: "Location",
  },
  labels: {
    icon: <Tag weight="fill" />,
    label: "Label",
  },
  education: {
    icon: <GraduationCap weight="fill" />,
    label: "Education",
  },
  skills: {
    icon: <BookOpenText weight="fill" />,
    label: "Skill",
  },
  interests: { icon: <ListStar weight="fill" />, label: "Interest" },
  languages: { icon: <Translate weight="fill" />, label: "Language" },
};

export const OUTREACH_CRM_EXPORT_OPTIONS = {
  campaign: {
    icon: PaperPlaneTilt,
  },
  list: {
    icon: Files,
  },
};

export const LEADS_TAB_ITEMS = [
  { label: "Leads", path: "" },
  { label: "Labels", path: "labels" },
];
